/**
 * Calendar Event Utilities
 *
 * Generates vCalendar/ICS format strings for QR codes
 */

import {
  epochMillisOfUtcInstant,
  formatUtcInstant,
  parseUtcInstant,
  systemClock,
} from "@ascleo/types/date-time-validity"

export interface CalendarEventData {
  title: string
  description?: string
  location?: string
  startTime: string // ISO 8601
  endTime: string // ISO 8601
  organizerName?: string
  organizerEmail?: string
}

/**
 * Format date for vCalendar (YYYYMMDDTHHMMSSZ)
 */
function formatDateForVCalendar(isoDate: string): string {
  const utcInstant = parseUtcInstant(isoDate)
  return utcInstant
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z')
}

/**
 * Generate a vCalendar/ICS format string for QR code
 *
 * This format is widely supported by calendar apps when scanned:
 * - iOS Calendar
 * - Google Calendar
 * - Outlook
 * - Most other calendar applications
 */
export function generateCalendarEvent(data: CalendarEventData): string {
  const generatedAt = formatUtcInstant(systemClock.now())
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Ascleo FrontDesk//EN',
    'BEGIN:VEVENT',
    `UID:${epochMillisOfUtcInstant(generatedAt)}@ascleo-frontdesk`,
    `DTSTAMP:${formatDateForVCalendar(generatedAt)}`,
    `DTSTART:${formatDateForVCalendar(data.startTime)}`,
    `DTEND:${formatDateForVCalendar(data.endTime)}`,
    `SUMMARY:${data.title}`
  ]

  if (data.description) {
    lines.push(`DESCRIPTION:${data.description}`)
  }

  if (data.location) {
    lines.push(`LOCATION:${data.location}`)
  }

  if (data.organizerName && data.organizerEmail) {
    lines.push(`ORGANIZER;CN=${data.organizerName}:mailto:${data.organizerEmail}`)
  }

  lines.push('STATUS:CONFIRMED')
  lines.push('SEQUENCE:0')
  lines.push('END:VEVENT')
  lines.push('END:VCALENDAR')

  return lines.join('\n')
}
